import { useTranslation } from "react-i18next";
import { RoomDetailsCardBottomProps } from "./types";

import {
  AttendeeAvailabilityStatus,
  MeetingRoom,
  RoomResource,
  formatDuration,
  getIsOnlineMeetingLinkSupported,
  markAsBookedLocally,
  useAppDispatch,
} from "@lib/store";
import { useAppSelector, useCreateEvent, useFormatDate } from "@hooks";

import { FlexCol, FlexRow, P, Span } from "@quarks";
import { ModalPageBottom } from "src/components/organisms/ModalPageBottom/ModalPageBottom";
import { Button } from "@molecules";
import { intervalToDuration } from "date-fns";
import { stripSecondsAndMilliseconds } from "@lib/utils";

const isResource = (room: MeetingRoom | RoomResource): room is RoomResource => {
  return "data" in room;
};

export const MeetingRoomDetailsCardBottom = ({
  dynamicTimeslot,
  meetingRoomSuggestion,
  room,
  onBookingDetailsPress,
  onBookNowCallback,
}: RoomDetailsCardBottomProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const displayName = isResource(room) ? room.name : room.displayName;
  const email = isResource(room) ? (room.data.email ?? "") : room.email;
  const { markedAsBookedLocally, scheduleAvailability } = meetingRoomSuggestion ?? {};

  const onlineMeetingLinkSupported = useAppSelector(getIsOnlineMeetingLinkSupported);

  const dynamicStartDateTime = dynamicTimeslot?.start?.toISOString() ?? "";
  const dynamicEndDateTime = dynamicTimeslot?.end?.toISOString() ?? "";
  const dynamicTimeslotFound = !!dynamicTimeslot?.start && !!dynamicTimeslot?.end;
  const start = useFormatDate(dynamicStartDateTime, "HH:mm");
  const end = useFormatDate(dynamicEndDateTime, "HH:mm");

  const handleOnSuccess = () => {
    dispatch(markAsBookedLocally({ buildingId: room.buildingId, roomId: room.id }));
    onBookNowCallback && onBookNowCallback();
  };

  const { createEvent, isCreatingEvent } = useCreateEvent(handleOnSuccess);

  const durationFormated =
    dynamicTimeslot?.start && dynamicTimeslot?.end
      ? intervalToDuration({
          start: stripSecondsAndMilliseconds(dynamicTimeslot.start),
          end: dynamicTimeslot.end,
        })
      : undefined;

  const durationAsString = formatDuration(t, durationFormated, false);

  const isBookableWithMapiq = isResource(room) ? room.data.isBookableWithMapiq : room.isBookableWithMapiq;
  const havePermissionForRoom =
    isBookableWithMapiq && scheduleAvailability && scheduleAvailability !== AttendeeAvailabilityStatus.Unknown;
  const canBook = dynamicTimeslotFound && !markedAsBookedLocally && havePermissionForRoom;

  return (
    <FlexRow gap={16}>
      {canBook ? (
        <FlexCol
          justifyContent="center"
          gap={8}
          alignItems="flex-start">
          <FlexRow>
            <P>
              <Span fontWeight="bold">{`${start} - ${end} `}</Span>
              {`(${durationAsString})`}
            </P>
          </FlexRow>
          {onBookingDetailsPress && (
            <Button
              onClick={onBookingDetailsPress}
              loading={isCreatingEvent}
              button={"text"}>
              {t("translation:BookingDetails")}
            </Button>
          )}
        </FlexCol>
      ) : havePermissionForRoom ? (
        <P alignSelf="center">{t("hereAndNow:RoomNotAvailable")}</P>
      ) : (
        <P alignSelf="center">{t("hereAndNow:NoPermission")}</P>
      )}
      <ModalPageBottom
        className="whitespace-nowrap"
        buttonLabel={t("translation:BookNow")}
        buttonLoading={isCreatingEvent}
        buttonDisabled={!canBook}
        style={{ padding: "0" }}
        onClick={() =>
          createEvent(
            {
              title: displayName,
              message: "",
              attendees: [],
              startDateTime: dynamicStartDateTime,
              endDateTime: dynamicEndDateTime,
              isOnlineMeeting: onlineMeetingLinkSupported,
              rooms: [email],
              isAllDay: false,
              organizerTimeZone: "",
              origin: "BookRoomNow",
            },
            t("hereAndNow:RoomBookingSuccessMessage"),
          )
        }
      />
    </FlexRow>
  );
};
